/**
 * Scenario API
 *
 * RTK Query endpoints for scenario operations (list, featured, start).
 */

import { api } from '../api';

// ============================================
// Types
// ============================================

export type ScenarioType =
  | 'pitch'
  | 'sales'
  | 'negotiation'
  | 'interview'
  | 'presentation'
  | 'networking'
  | 'storytelling'
  | 'objection_handling'
  | 'public_speaking'
  | 'custom';

export type ScenarioDifficulty = 'beginner' | 'intermediate' | 'advanced' | 'expert';

export type CoachPersona =
  | 'encouraging'
  | 'investor'
  | 'prospect'
  | 'storytelling'
  | 'objection_drill'
  | 'public_speaking'
  | 'networking'
  | 'nonprofit_donor';

export interface Scenario {
  id: string;
  title: string;
  description: string;
  type: ScenarioType;
  difficulty: ScenarioDifficulty;
  persona: CoachPersona;
  systemPrompt?: string;
  initialMessage?: string;
  objectives: string[];
  tags: string[];
  estimatedMinutes: number;
  organizationId?: string;
  isPublic: boolean;
  isActive: boolean;
  usageCount: number;
  averageRating?: number;
  /**
   * Modalities a graded (Testing) attempt may be taken in: those the
   * scenario's author named a compatible rubric for. Empty: not gradable.
   */
  testingModalities?: string[];
  /**
   * Modalities a Development attempt may be taken in: those the authored
   * rubric covers; every modality when the scenario authored none (general
   * rubric). Never a general rubric in place of an authored one.
   */
  developmentModalities?: string[];
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

// ============================================
// API Definition
// ============================================

export const scenarioApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * List scenarios with pagination and filters
     */
    listScenarios: builder.query<
      { scenarios: Scenario[]; total: number; page: number; totalPages: number },
      {
        page?: number; limit?: number; type?: ScenarioType; difficulty?: ScenarioDifficulty; persona?: CoachPersona;
        /** Scenarios carrying ANY of these tags (e.g. an exam's relatedScenarioKeys). */
        tags?: string[];
      }
    >({
      query: ({ page = 1, limit = 20, type, difficulty, persona, tags }) => {
        const params = new URLSearchParams();
        params.append('page', String(page));
        params.append('limit', String(limit));
        if (type) params.append('type', type);
        if (difficulty) params.append('difficulty', difficulty);
        if (persona) params.append('persona', persona);
        for (const tag of tags ?? []) params.append('tag', tag);
        return `/scenarios?${params.toString()}`;
      },
      transformResponse: (response: ApiResponse<Scenario[]>) => ({
        scenarios: response.data,
        total: response.meta?.total ?? 0,
        page: response.meta?.page ?? 1,
        totalPages: response.meta?.totalPages ?? 1,
      }),
      providesTags: ['Scenarios'],
    }),

    /**
     * Get featured scenarios (top 10 by usage)
     */
    getFeaturedScenarios: builder.query<Scenario[], void>({
      query: () => '/scenarios/featured',
      transformResponse: (response: ApiResponse<Scenario[]>) => response.data,
      providesTags: ['Scenarios'],
    }),

    /**
     * Get a single scenario
     */
    getScenario: builder.query<Scenario, string>({
      query: (id) => `/scenarios/${id}`,
      transformResponse: (response: ApiResponse<Scenario>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'Scenarios', id }],
    }),
  }),
  overrideExisting: false,
});

// Export hooks
export const {
  useListScenariosQuery,
  useGetFeaturedScenariosQuery,
  useGetScenarioQuery,
} = scenarioApi;
